/**
 * Quelles adresses de donoor.org les moteurs doivent-ils indexer, et dans
 * quelles langues ? Source UNIQUE pour le plan du site (sitemap-langues.ts),
 * les balises hreflang (plugins/hreflang.ts) et la balise robots
 * (useRobotsContent).
 *
 * Audit SEO du 18/10/2026 : 79 % du plan du site (2 310 adresses sur 2 911)
 * étaient des copies traduites, dont des pages d'annuaire local de 30 mots
 * (« /de/associations/ville/pessac ») et des pages techniques (« /de/pay/… »)
 * — recréées par la traduction alors que leur version française était exclue.
 * Google peut y voir du contenu produit en masse et baisser la confiance dans
 * tout le site.
 *
 * Règle retenue (Karim 18/10 : « l'idéal pour un SEO excellent ») :
 *   • les pages produit, fiches d'associations, cagnottes, événements,
 *     boutiques et adhésions restent indexées dans les 6 langues (leurs textes
 *     sont traduits) ;
 *   • l'annuaire LOCAL (villes, départements, régions, listes « toutes les
 *     cagnottes de l'asso », recherche) n'est indexé qu'en français : personne
 *     ne cherche « Vereine in Pessac ». Les versions traduites restent
 *     accessibles aux visiteurs, en `noindex, follow`, hors plan du site ;
 *   • les pages techniques ne sont jamais déclarées, dans aucune langue.
 *
 * Tous les chemins sont SANS préfixe de langue (cf. cheminSansPrefixeLangue).
 */

const PREFIXES_LANGUE = ["en", "de", "es", "it", "nl"];

/**
 * « /de/pay/x » → « /pay/x », « /de » → « / ». Même règle que
 * cheminSansLangue (app/utils/langue.ts), utilisable côté serveur.
 */
export function cheminSansPrefixeLangue(chemin: string): string {
  const [, premier = "", ...reste] = chemin.split("/");
  if (!PREFIXES_LANGUE.includes(premier)) return chemin;
  return `/${reste.join("/")}`;
}

/** Pages transactionnelles ou techniques : jamais dans le plan du site, aucune langue. */
const PREFIXES_TECHNIQUES = ["/pay", "/embed", "/embed-preview", "/donation-success", "/erreur", "/register", "/app"];

export function estCheminTechnique(chemin: string): boolean {
  // `/recherche` seul est l'OUTIL de recherche (noindex) ; ses sous-pages
  // (/recherche/cagnottes…) sont de vraies listes, traitées plus bas.
  if (chemin === "/recherche") return true;
  return PREFIXES_TECHNIQUES.some((p) => chemin === p || chemin.startsWith(`${p}/`));
}

const ANNUAIRE_LOCAL: readonly RegExp[] = [
  // Hubs géographiques des associations.
  /^\/associations\/(ville|departement|region)\/[^/]+/,
  // /associations/<ville>/<catégorie> et /associations/<asso>/<cagnottes|billetterie|…>.
  /^\/associations\/[^/]+\/[^/]+/,
  // Hubs ville des cagnottes, événements, adhésions et boutiques.
  /^\/(cagnottes|evenements|adhesions|boutiques)\/[^/]+$/,
  // Listes de recherche (/recherche/cagnottes, pagination comprise).
  /^\/recherche\/.+/,
];

/** Page d'annuaire local : indexée en français seulement. */
export function estIndexeeEnFrancaisSeulement(chemin: string): boolean {
  return ANNUAIRE_LOCAL.some((motif) => motif.test(chemin));
}
